import jaCommon from '@langs/ja/common.json'
import jaAuth from '@langs/ja/auth.json'
import jaVault from '@langs/ja/vault.json'
import jaHome from '@langs/ja/home.json'
import jaReports from '@langs/ja/reports.json'
import jaTools from '@langs/ja/tools.json'
import jaSettings from '@langs/ja/settings.json'
import jaSend from '@langs/ja/send.json'
import jaLanding from '@langs/ja/landing.json'
import jaDocs from '@langs/ja/docs.json'
import jaContact from '@langs/ja/contact.json'
import jaDownload from '@langs/ja/download.json'
import jaMarketing from '@langs/ja/marketing.json'
import jaSupport from '@langs/ja/support.json'
import enCommon from '@langs/en/common.json'
import enAuth from '@langs/en/auth.json'
import enVault from '@langs/en/vault.json'
import enHome from '@langs/en/home.json'
import enReports from '@langs/en/reports.json'
import enTools from '@langs/en/tools.json'
import enSettings from '@langs/en/settings.json'
import enSend from '@langs/en/send.json'
import enLanding from '@langs/en/landing.json'
import enDocs from '@langs/en/docs.json'
import enContact from '@langs/en/contact.json'
import enDownload from '@langs/en/download.json'
import enMarketing from '@langs/en/marketing.json'
import enSupport from '@langs/en/support.json'

/**
 * 翻訳辞書の集約。新しい namespace (例: vault, send) を増やしたら
 * `@langs/<lang>/<namespace>.json` を作成し、ここに 1 行追加するだけで使える。
 *
 * key 形式は `<namespace>.<dot.path>` (例: `common.nav.home`、`auth.error.unauthorized`)。
 */

export const SUPPORTED_LANGUAGES = ['ja', 'en'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]
export const DEFAULT_LANGUAGE: Language = 'ja'

export const isLanguage = (value: unknown): value is Language =>
  typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)

type Dictionary = Record<string, unknown>

const messages: Record<Language, Record<string, Dictionary>> = {
  ja: {
    common: jaCommon,
    auth: jaAuth,
    vault: jaVault,
    home: jaHome,
    reports: jaReports,
    tools: jaTools,
    settings: jaSettings,
    send: jaSend,
    landing: jaLanding,
    docs: jaDocs,
    contact: jaContact,
    download: jaDownload,
    marketing: jaMarketing,
    support: jaSupport,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    vault: enVault,
    home: enHome,
    reports: enReports,
    tools: enTools,
    settings: enSettings,
    send: enSend,
    landing: enLanding,
    docs: enDocs,
    contact: enContact,
    download: enDownload,
    marketing: enMarketing,
    support: enSupport,
  },
}

const getDeep = (obj: unknown, path: readonly string[]): string | undefined => {
  let cur: unknown = obj
  for (const seg of path) {
    if (typeof cur !== 'object' || cur === null) return undefined
    cur = (cur as Record<string, unknown>)[seg]
  }

  return typeof cur === 'string' ? cur : undefined
}

const interpolate = (template: string, vars: Record<string, string | number>): string =>
  template.replace(/\{(\w+)\}/g, (_, name: string) =>
    name in vars ? String(vars[name]) : `{${name}}`,
  )

/**
 * 指定 key prefix 配下の全 string leaf を収集する (例: docs 検索インデックス構築)。
 * prefix が string を直接指す場合はその 1 件を返し、見つからなければ空配列。
 * 補間 (vars) は行わない — 生のテンプレート文字列をそのまま集める。
 */
export const collectStrings = (language: Language, keyPrefix: string): string[] => {
  const path = keyPrefix.split('.')
  let cur: unknown = messages[language]
  for (const seg of path) {
    if (typeof cur !== 'object' || cur === null) return []
    cur = (cur as Record<string, unknown>)[seg]
  }

  const out: string[] = []

  const walk = (node: unknown): void => {
    if (typeof node === 'string') {
      out.push(node)

      return
    }
    if (typeof node === 'object' && node !== null) {
      for (const v of Object.values(node)) walk(v)
    }
  }

  walk(cur)

  return out
}

/**
 * `<namespace>.<dot.path>` 形式の key を解決する。
 * 見つからない場合は `ja` (DEFAULT_LANGUAGE) にフォールバック、それでも無ければ key 文字列をそのまま返す
 * (デバッグしやすさを優先)。
 */
export const translate = (
  language: Language,
  key: string,
  vars?: Record<string, string | number>,
): string => {
  const path = key.split('.')
  const found = getDeep(messages[language], path) ?? getDeep(messages[DEFAULT_LANGUAGE], path)
  const value = found ?? key

  return vars ? interpolate(value, vars) : value
}
